import { useContext, useEffect, useMemo, useState } from 'react'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { com, Nullable } from '@eidu/form'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import { fieldValueToString } from '../../domain/entity/DraftFieldValue'
import EntityRepository, { setGlobalAuthContext } from '../../io/EntityRepository'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import type { HierarchyLevelConfig } from './HierarchyPage'
import { ADMIN_SCHEMA_NAME, formatTypeName, getTypeDisplayName } from '../../util/entityType/typeNameSchema'

import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import Field = com.eidu.sharedlib.entity.field.Field
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue

export const HIERARCHIES_TYPE_NAME = formatTypeName(ADMIN_SCHEMA_NAME, 'Hierarchies')
export const HIERARCHY_NAME_FALLBACK = 'Hierarchy'

const MAX_LEVELS = 20
const normalize = (value: string) => value.trim().toLowerCase()

const hasPrimaryLabelField = (entityType: EntityType): boolean => {
  const fields = entityType.fields.asJsReadonlyArrayView()
  const argumentFields = entityType.label.primary.argumentFields.asJsReadonlyArrayView()
  if (argumentFields.length === 0) return false
  return argumentFields.some((fieldId) => fields.some((field) => field.id.equals(fieldId)))
}

const findEntityTypeByName = (
  entityTypes: ReadonlyMap<EntityTypeId, EntityType | null>,
  targetName: string
): EntityType | undefined => {
  const normalizedTarget = normalize(targetName)
  const types = Array.from(entityTypes.values()).filter((type): type is EntityType => !!type)
  const exactMatch = types.find((type) => normalize(type.name) === normalizedTarget)
  if (exactMatch) return exactMatch
  return types.find((type) => normalize(getTypeDisplayName(type.name)) === normalizedTarget)
}

const findFieldByNameCaseInsensitive = (entityType: EntityType, targetName: string): Field | undefined => {
  const normalizedTarget = normalize(targetName)
  return entityType.fields.asJsReadonlyArrayView().find((field) => normalize(field.name) === normalizedTarget)
}

type HierarchyListItem = {
  id: string
  name: string
}

type CustomHierarchyBuildResult = {
  name: string
  levels: readonly HierarchyLevelConfig[]
  errors: string[]
}

const getFieldValue = (valuesByFieldId: ReadonlyMap<FieldId, Nullable<FieldValue>>, field: Field): string =>
  fieldValueToString(valuesByFieldId.get(field.id) ?? null).trim()

const referencesParentType = (field: Field, parentTypeId: EntityTypeId | undefined): boolean => {
  if (!parentTypeId) return false
  const validReferenceTypes = field.validReferenceTypes?.asJsReadonlySetView()
  if (!validReferenceTypes) return false
  return Array.from(validReferenceTypes).some((typeId) => typeId.equals(parentTypeId))
}

export const buildHierarchyFromEntity = (
  hierarchy: EntityWithLabelAndRelated,
  entityTypes: ReadonlyMap<EntityTypeId, EntityType | null>
): CustomHierarchyBuildResult => {
  const errors: string[] = []
  const levels: HierarchyLevelConfig[] = []

  const hierarchyType = entityTypes.get(hierarchy.entity.typeId)
  if (hierarchyType === null) {
    errors.push(
      'Hierarchy type exists but its metadata could not be loaded. Check permissions or if the type was deleted.'
    )
    return { name: HIERARCHY_NAME_FALLBACK, levels, errors }
  }
  if (!hierarchyType) {
    errors.push(`Hierarchy type "${HIERARCHIES_TYPE_NAME}" is not available. Ensure the type exists and is readable.`)
    return { name: HIERARCHY_NAME_FALLBACK, levels, errors }
  }

  const valuesByFieldId = hierarchy.entity.valuesByFieldId.asJsReadonlyMapView()

  const nameField = findFieldByNameCaseInsensitive(hierarchyType, 'Name')
  const resolvedName =
    (nameField ? getFieldValue(valuesByFieldId, nameField) : '') ||
    hierarchy.primaryLabelText.trim() ||
    HIERARCHY_NAME_FALLBACK

  let previousType: EntityType | undefined

  for (let levelIndex = 1; levelIndex <= MAX_LEVELS; levelIndex++) {
    const typeLabel = `Level ${levelIndex} Type`
    const parentFieldLabel = `Level ${levelIndex} Parent Field`
    const typeField = findFieldByNameCaseInsensitive(hierarchyType, typeLabel)

    const typeIsRequired = levelIndex <= 2
    if (!typeField) {
      if (typeIsRequired) errors.push(`Field "${typeLabel}" is missing on the ${HIERARCHIES_TYPE_NAME} type.`)
      break
    }

    const typeName = getFieldValue(valuesByFieldId, typeField)
    if (!typeName) {
      if (typeIsRequired) errors.push(`"${typeLabel}" must be provided.`)
      break
    }

    const entityType = findEntityTypeByName(entityTypes, typeName)
    if (!entityType) {
      errors.push(`Entity type "${typeName}" (from ${typeLabel}) was not found.`)
      break
    }

    if (!hasPrimaryLabelField(entityType)) {
      errors.push(`"${getTypeDisplayName(entityType.name)}" does not have a primary label field configured.`)
      break
    }

    let parentFieldId = undefined
    if (previousType) {
      const parentFieldOnHierarchy = findFieldByNameCaseInsensitive(hierarchyType, parentFieldLabel)
      if (!parentFieldOnHierarchy) {
        errors.push(`Field "${parentFieldLabel}" is missing on the ${HIERARCHIES_TYPE_NAME} type.`)
        break
      }

      const parentFieldName = getFieldValue(valuesByFieldId, parentFieldOnHierarchy)
      if (!parentFieldName) {
        errors.push(`"${parentFieldLabel}" must be provided.`)
        break
      }

      const parentField = findFieldByNameCaseInsensitive(entityType, parentFieldName)
      if (!parentField) {
        errors.push(
          `Field "${parentFieldName}" (from ${parentFieldLabel}) was not found on "${getTypeDisplayName(
            entityType.name
          )}".`
        )
        break
      }

      if (parentField.type !== FieldType.Reference) {
        errors.push(`"${parentField.name}" on "${getTypeDisplayName(entityType.name)}" must be a reference field.`)
        break
      }

      if (!referencesParentType(parentField, previousType.id)) {
        errors.push(
          `"${parentField.name}" on "${getTypeDisplayName(entityType.name)}" must reference "${getTypeDisplayName(
            previousType.name
          )}".`
        )
        break
      }

      parentFieldId = parentField.id
    }

    levels.push({
      levelName: getTypeDisplayName(entityType.name),
      entityTypeId: entityType.id,
      parentFieldId,
      effectiveParentFieldId: parentFieldId,
    })
    previousType = entityType
  }

  if (levels.length === 0 && errors.length === 0) errors.push('No hierarchy levels are configured.')

  return { name: resolvedName, levels, errors }
}

const findFieldByName = (entityType: EntityType, targetName: string): Field | undefined =>
  entityType.fields.asJsReadonlyArrayView().find((field) => field.name.toLowerCase() === targetName.toLowerCase())

export const useHierarchiesList = () => {
  const entityTypeRepository = useContext(EntityTypesContext)
  const authContext = useContext<IAuthContext>(AuthContext)

  const entityTypes = entityTypeRepository.useAll()
  const entityTypesLoading = entityTypeRepository.useLoading()
  const entityTypesError = entityTypeRepository.useError()

  const hierarchyType = useMemo(
    () => (entityTypes ? findEntityTypeByName(entityTypes, HIERARCHIES_TYPE_NAME) : undefined),
    [entityTypes]
  )

  const [hierarchies, setHierarchies] = useState<HierarchyListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(undefined)

  useEffect(() => {
    setGlobalAuthContext(authContext)
  }, [authContext])

  useEffect(() => {
    let cancelled = false

    const nameField = hierarchyType ? findFieldByName(hierarchyType, 'Name') : undefined

    const loadHierarchies = async (typeId: EntityTypeId, types: ReadonlyMap<EntityTypeId, EntityType>) => {
      setLoading(true)
      try {
        const repository = new EntityRepository({ typeId, types, pageSize: 500 })
        const all = await repository.getAll()

        const items: HierarchyListItem[] = Array.from(all.values())
          .filter((entity): entity is EntityWithLabelAndRelated => entity !== null)
          .map((entity) => {
            const values = entity.entity.valuesByFieldId.asJsReadonlyMapView()
            const nameFromField = nameField ? fieldValueToString(values.get(nameField.id) ?? null).trim() : ''

            return {
              id: entity.entity.id.toString(),
              name: nameFromField || entity.primaryLabelText.trim() || HIERARCHY_NAME_FALLBACK,
            }
          })
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))

        if (!cancelled) {
          setHierarchies(items)
          setError(undefined)
        }
      } catch (err) {
        if (!cancelled) setError(err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    if (hierarchyType && entityTypes) loadHierarchies(hierarchyType.id, entityTypes)
    else {
      setHierarchies([])
      setLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [hierarchyType, entityTypes])

  return {
    hierarchies,
    loading: entityTypesLoading || loading,
    error: error ?? entityTypesError,
  }
}

export type { HierarchyListItem }
